/**
 * Cash Flow Dashboard - Utility Functions
 */

import {
    CashFlowDetailRow,
    CashFlowTreeNode,
    CashFlowWeeklyRow,
    CashFlowWeeklyNode,
} from './types';

/**
 * Format a number using Indonesian locale (no decimals).
 * Zero / null / NaN render as an en dash so empty rows stay quiet.
 */
export const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '–';
    if (!Number.isFinite(value)) return '–';
    if (value === 0) return '–';
    return value.toLocaleString('id-ID', { maximumFractionDigits: 0 });
};

/**
 * Compact currency formatting for chart axes / tooltips (K, M, B, T).
 * Example: 1234567890 -> "1,2 B".
 */
export const formatCompact = (value: number | null | undefined): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '0';
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    const fmt = (n: number) => n.toLocaleString('id-ID', { maximumFractionDigits: 1 });
    if (abs >= 1e12) return `${sign}${fmt(abs / 1e12)} T`;
    if (abs >= 1e9) return `${sign}${fmt(abs / 1e9)} B`;
    if (abs >= 1e6) return `${sign}${fmt(abs / 1e6)} M`;
    if (abs >= 1e3) return `${sign}${fmt(abs / 1e3)} K`;
    return `${sign}${fmt(abs)}`;
};

/**
 * Depth of a hierarchical code, e.g. "1.2.3" -> 3.
 */
export const codeDepth = (code: string): number => code.split('.').length;

/**
 * Parent code, e.g. "1.2.3" -> "1.2"; top-level returns ''.
 */
export const parentCode = (code: string): string => {
    const parts = code.split('.');
    parts.pop();
    return parts.join('.');
};

/**
 * Build a hierarchical tree from the flat, pre-ordered detail rows.
 * Each node keeps its own values; headers already carry rolled-up totals
 * from the backend, so we do not re-sum here.
 */
export const buildTree = (rows: CashFlowDetailRow[]): CashFlowTreeNode[] => {
    const nodeByCode = new Map<string, CashFlowTreeNode>();
    const roots: CashFlowTreeNode[] = [];

    rows.forEach((row) => {
        nodeByCode.set(row.code, {
            ...row,
            depth: codeDepth(row.code),
            children: [],
        });
    });

    rows.forEach((row) => {
        const node = nodeByCode.get(row.code)!;
        const parent = nodeByCode.get(parentCode(row.code));
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    });

    return roots;
};

/**
 * Flatten a monthly tree into ordered rows for export / rendering.
 */
export const flattenTree = (nodes: CashFlowTreeNode[]): CashFlowTreeNode[] => {
    const out: CashFlowTreeNode[] = [];
    const walk = (list: CashFlowTreeNode[]) => {
        list.forEach((n) => {
            out.push(n);
            if (n.children.length) walk(n.children);
        });
    };
    walk(nodes);
    return out;
};

/**
 * Merge weekly realized rows with the monthly detail (for totals) into a tree.
 * Budget is monthly only (weekly budget is intentionally 0 on the backend),
 * so weekly columns show realized; the Total columns use the monthly detail.
 */
export const buildWeeklyTree = (
    weeklyRows: CashFlowWeeklyRow[],
    detailRows: CashFlowDetailRow[],
): CashFlowWeeklyNode[] => {
    const detailByCode = new Map<string, CashFlowDetailRow>();
    detailRows.forEach((d) => detailByCode.set(d.code, d));

    // Aggregate realized per code per week.
    const weeksByCode = new Map<string, number[]>();
    const metaByCode = new Map<string, { name: string; is_header: boolean }>();
    weeklyRows.forEach((w) => {
        if (!weeksByCode.has(w.code)) {
            weeksByCode.set(w.code, [0, 0, 0, 0]);
            metaByCode.set(w.code, { name: w.name, is_header: w.is_header });
        }
        const idx = Math.min(Math.max(w.week_no, 1), 4) - 1;
        weeksByCode.get(w.code)![idx] += w.realized || 0;
    });

    // Codes come from the detail set (full tree); weekly rows mirror them.
    const nodeByCode = new Map<string, CashFlowWeeklyNode>();
    const roots: CashFlowWeeklyNode[] = [];

    detailRows.forEach((d) => {
        const weeks = weeksByCode.get(d.code) || [0, 0, 0, 0];
        nodeByCode.set(d.code, {
            code: d.code,
            name: d.name,
            is_header: d.is_header,
            depth: codeDepth(d.code),
            weeks,
            totalBudget: d.budget,
            totalRealized: d.realized,
            totalUnrealized: d.unrealized,
            children: [],
        });
    });

    detailRows.forEach((d) => {
        const node = nodeByCode.get(d.code)!;
        const parent = nodeByCode.get(parentCode(d.code));
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    });

    return roots;
};

/**
 * Month options for the filter (Indonesian month names).
 */
export const getMonthOptions = () => [
    { value: 1, label: 'Januari' },
    { value: 2, label: 'Februari' },
    { value: 3, label: 'Maret' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mei' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'Agustus' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Desember' },
];

export const getMonthName = (month: number): string => {
    const opt = getMonthOptions().find((m) => m.value === month);
    return opt ? opt.label : '';
};

/**
 * Year options: last 5 years + current + next.
 */
export const getYearOptions = (): number[] => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let i = currentYear + 1; i >= currentYear - 5; i--) {
        years.push(i);
    }
    return years;
};

/**
 * Format a 'YYYYMM' period into a short label like "Nov 25".
 */
export const formatPeriodeShort = (periode: string): string => {
    if (!periode || periode.length !== 6) return periode;
    const year = periode.slice(2, 4);
    const month = Number.parseInt(periode.slice(4, 6), 10);
    const short = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    return `${short[month - 1] || periode} ${year}`;
};
